import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Phone, ArrowRight, ArrowUpRight, Home as HomeIcon, FileDown } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { Marquee } from "../components/Marquee";
import { OrderForm } from "../components/OrderForm";
import { Faq } from "../components/sections/Faq";
import { VisaCountries } from "../components/sections/VisaCountries";
import { CountrySearch } from "../components/CountrySearch";
import { Reveal, StaggerLines } from "../components/Reveal";
import { SEO_PAGES } from "../data/seoPages";
import { PHONE, PHONE_HREF, IMAGES, FAQS } from "../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function ServicePage() {
  const { slug } = useParams();
  const page = SEO_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const heroImg = IMAGES[page.heroImage] || IMAGES.traveler;
  const pageFaqs = FAQS.filter((f) => page.faqKeys?.includes(f.q));
  const related = Object.keys(SEO_PAGES).filter((s) => s !== slug).slice(0, 3);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.h1,
    description: page.metaDescription,
    provider: { "@type": "LocalBusiness", name: "Urgent Passport & Visa", telephone: "+1-202-457-4665" },
    areaServed: ["Washington DC", "Maryland", "Virginia", "United States"],
    url: `https://upvservices.com/${slug}`,
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pageFaqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://upvservices.com/" },
      { "@type": "ListItem", position: 2, name: page.h1, item: `https://upvservices.com/${slug}` },
    ],
  };

  return (
    <div data-testid="service-page">
      <Seo title={page.metaTitle} description={page.metaDescription} path={`/${slug}`} jsonLd={[serviceSchema, faqSchema, breadcrumb]} />
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="relative bg-[#0b192c] text-white overflow-hidden" data-testid="service-hero">
          <div className="noise-overlay" />
          <div className="absolute inset-0">
            <img src={heroImg} alt={page.h1} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b192c] via-[#0b192c]/90 to-[#0b192c]/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span>
              <span className="text-white/90">{page.overline}</span>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">{page.overline}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight max-w-3xl">
              <StaggerLines lines={[page.h1]} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed"
            >
              {page.intro}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.6 }} className="mt-8 flex flex-wrap gap-4">
              <a href="#quote" className="inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-7 py-4 font-semibold hover:bg-[#ef233c] transition-colors" data-testid="service-cta-quote">
                Place Your Order <ArrowRight className="w-4 h-4" />
              </a>
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white px-7 py-[14px] font-semibold hover:bg-white hover:text-[#0b192c] transition-colors" data-testid="service-cta-call">
                <Phone className="w-4 h-4" /> Call for a Quote: {PHONE}
              </a>
            </motion.div>
            {slug === "visa-services-washington-dc" && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }} className="mt-8">
                <CountrySearch />
              </motion.div>
            )}
          </div>
        </section>

        <Marquee />

        {/* Content + form */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="grid sm:grid-cols-2 gap-4 mb-14">
                  {page.bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid={`service-bullet-${i}`}>
                      <span className="shrink-0 w-6 h-6 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#d90429]" />
                      </span>
                      <span className="text-[15px] text-[#0b192c] font-medium">{b}</span>
                    </div>
                  ))}
                </div>
              </Reveal>

              <div className="space-y-12">
                {page.sections.map((s, i) => (
                  <Reveal key={i} delay={0.05}>
                    <article>
                      <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight">{s.h2}</h2>
                      <p className="mt-4 text-slate-600 leading-relaxed text-[17px]">{s.body}</p>
                    </article>
                  </Reveal>
                ))}
              </div>

              {page.eligibility && (
                <Reveal>
                  <div className="mt-12 rounded-2xl border border-slate-200 bg-[#f8f9fa] p-7" data-testid="eligibility">
                    <h2 className="font-display font-bold text-xl md:text-2xl text-[#0b192c]">{page.eligibility.heading}</h2>
                    <ul className="mt-5 space-y-3">
                      {page.eligibility.items.map((it, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-700 text-[16px]">
                          <span className="shrink-0 w-5 h-5 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-1"><Check className="w-3 h-3 text-[#d90429]" /></span>
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}

              {page.forms && (
                <Reveal>
                  <div className="mt-8" data-testid="forms-section">
                    <h2 className="font-display font-bold text-xl md:text-2xl text-[#0b192c] mb-4">Downloadable Forms</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {page.forms.map((f, i) => (
                        <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3.5 hover:border-[#d90429] hover:bg-[#f8f9fa] transition-colors" data-testid={`form-${i}`}>
                          <FileDown className="w-5 h-5 text-[#d90429] shrink-0" />
                          <span className="text-sm font-medium text-[#0b192c] group-hover:text-[#d90429] transition-colors">{f.name}</span>
                        </a>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-slate-500">Forms open as PDFs. Not sure which you need? Call {PHONE} and we'll guide you.</p>
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-24" id="quote">
                <OrderForm sourcePage={slug} />
              </div>
            </div>
          </div>
        </section>

        {slug === "visa-services-washington-dc" && <VisaCountries />}

        {pageFaqs.length > 0 && <Faq items={pageFaqs} />}

        {/* Related services */}
        <section className="bg-[#f8f9fa] py-20 md:py-28" data-testid="related-services">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight mb-10">Related services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((s) => (
                <Link
                  key={s}
                  to={`/${s}`}
                  onClick={scrollTop}
                  className="group bg-white rounded-2xl p-7 border border-black/5 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(11,25,44,0.10)] transition-[transform,box-shadow] duration-300"
                  data-testid={`related-${s}`}
                >
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">{SEO_PAGES[s].overline}</span>
                  <h3 className="mt-3 font-display font-bold text-xl text-[#0b192c]">{SEO_PAGES[s].h1}</h3>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0b192c] group-hover:gap-2 transition-[gap]">
                    Learn more <ArrowUpRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
